// A contact's calls, opened the same way the AI Calls screen opens one.
//
// WHY THIS REUSES AudioSessionModal RATHER THAN DRAWING ITS OWN CONVERSATION.
//
// The AI Calls screen already gets both halves right, and neither is obvious.
// The transcript is not on the switch's audio_sessions row any more — that
// column stopped being written in late August — it is backfilled server-side
// from the post-call events by matching call_id to call_sid. The recording is
// not simply session.recording_url either: the stored URL on this estate points
// at the SWITCH, behind HTTP Basic, which a browser can never play, so it goes
// through recordingsService.resolvePlayableUrl, which tries the signed cpass
// copy and the CDN in the right order for whichever backend is serving.
//
// A second implementation of that here would be a second place to get it wrong,
// and the modal also carries the call detail fetch, the "why nothing played"
// message and the captures panel. So a call opens the SAME component, and
// anything fixed on AI Calls is fixed here for free.
//
// WHAT THE FALLBACK IS FOR. Opening by call id needs an audio_sessions row, and
// for agent calls there often is not one — measured on the leads queue: of 173
// calls carrying an id, 0 had a session row and 172 had an indexed recording.
// Those calls still have a conversation, in agent_call_events, which is what
// /v1/agent-calls/{callSid}/transcript hands back. Without this branch such a
// call would report itself as gone, which is what the leads screen used to do.
//
// EVERYTHING IS BEHIND A CLICK. ContactPeekModal was written with no history at
// all because a contact's calls are federated from two switches over SSH
// tunnels, and making every peek pay that — including the many opened only to
// read a phone number — was the cost it refused. So nothing here fetches on
// mount. That is a deliberate design constraint, not an oversight to optimise
// away.
import React, { useCallback, useState } from 'react';
import { Loader2, Play, ChevronRight, ChevronDown } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { contactsService } from 'api/services/contacts.service';
import { sessionsService } from 'api/services/sessions.service';
import { agentCallsService } from 'api/services/crmProviders.service';
import { recordingsService } from 'api/services/recordings.service';
import AudioSessionModal from 'components/Modals/AudioSessionModal';
import TranscriptViewer from 'components/Common/TranscriptViewer';
import AudioPlayer from 'components/Common/AudioPlayer';

/** One row of the calls channel of the contact timeline. */
interface CallRow {
  at?: string;
  direction?: string;
  durationSeconds?: number;
  callSid?: string | null;
  recordingUrl?: string | null;
  /** The server knows whether a conversation exists; it does not send one. */
  hasTranscript?: boolean;
  ref?: string;
}

const when = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const duration = (s?: number) =>
  s == null ? '' : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;

/**
 * The organisation to act on, the way the AI Calls screen scopes a recording.
 *
 * NOT useScopedOrgId: that hook is for pages carrying an organisation FILTER,
 * and it deliberately returns '' for a provider-level role until they pick one
 * from a selector this modal does not have. The peek has no picker, so the
 * honest rule is the one the hook's own header states — you act on your own
 * organisation — which is exactly what the AI Calls screen falls back to.
 *
 * 'synthetic-org' is a placeholder, not an organisation; sending it as a real id
 * is worse than sending none, which is why that guard is copied rather than
 * simplified away.
 */
function useActingOrgId(): string | undefined {
  const { currentOrganization } = useAuth();
  const id = currentOrganization?.id;
  return id && id !== 'synthetic-org' ? id : undefined;
}

const ContactCalls: React.FC<{ contactId: string; phones?: string[] }> = ({ contactId, phones = [] }) => {
  const organizationId = useActingOrgId();
  const [sessions, setSessions] = useState<any[] | null>(null);
  const [calls, setCalls] = useState<CallRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Says the list is short for a reason the reader cannot otherwise see: the
  // server sets this when a switch did not answer. Without it an incomplete
  // history is indistinguishable from a quiet customer.
  const [partial, setPartial] = useState<string | null>(null);

  // The call opened in the shared modal, when it resolved to a session row.
  const [session, setSession] = useState<any | null>(null);

  /**
   * The contact's calls, asked for the way the AI Calls screen asks.
   *
   * THE AUDIO SESSIONS FIRST. That is the list AI Calls renders, and a row from
   * it opens AudioSessionModal directly with the transcript and the recording
   * already resolvable — no second lookup, no guessing. The endpoint takes
   * callerId and dialedNumber, so a contact's numbers are matched on both: an
   * outbound call has this person as the DIALLED party, and filtering on the
   * caller alone would silently drop every one of them.
   *
   * THE TIMELINE ONLY IF THAT IS EMPTY. Agent calls frequently have no
   * audio_sessions row at all — measured at 0 of 173 on the leads queue — and a
   * peek that showed nothing for them would be worse than one that shows the
   * federated call list and opens what it can. So the timeline is the fallback,
   * not the primary, and the primary is exactly what AI Calls does.
   */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const numbers = phones.filter(Boolean);
    if (numbers.length) {
      try {
        const results = await Promise.all(
          numbers.flatMap((n) => [
            sessionsService.getAudioSessions({ callerId: n, perPage: 20, organizationId }),
            sessionsService.getAudioSessions({ dialedNumber: n, perPage: 20, organizationId }),
          ]),
        );
        // Merged and de-duplicated: a call where both parties are this
        // contact's numbers comes back from more than one of these.
        // A plain object rather than a Map: this tsconfig targets es5 and
        // spreading a Map iterator needs downlevelIteration, which is not a
        // compiler flag worth flipping for one de-duplication.
        const byId: Record<string, any> = {};
        for (const r of results) {
          const rows: any[] = (r as any)?.data?.data || (r as any)?.data?.content || [];
          rows.forEach((row, n) => {
            const key = String(row.id ?? row.session_ref ?? row.call_id ?? `row-${n}`);
            if (!byId[key]) byId[key] = row;
          });
        }
        const merged = Object.values(byId);
        if (merged.length > 0) {
          setSessions(merged.sort(
            (a, b) => String(b.start_time ?? b.created_at ?? '').localeCompare(
              String(a.start_time ?? a.created_at ?? ''))));
          setLoading(false);
          return;
        }
      } catch {
        // Fall through to the timeline rather than reporting nothing: an
        // unreachable sessions endpoint is not evidence of a quiet customer.
      }
    }

    const res = await contactsService.getTimeline(contactId, 'calls', 0, 20);
    if (res.success && res.data) {
      const d: any = res.data;
      setCalls(Array.isArray(d.events) ? d.events : []);
      setPartial(d.partial ? (d.note || 'Some calls may be missing.') : null);
    } else {
      setError(res.error || 'Could not load this contact’s calls.');
    }
    setLoading(false);
  }, [contactId, phones, organizationId]);

  if (sessions === null && calls === null) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-[12px] inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400
                     hover:underline disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {loading ? 'Loading calls…' : 'Show calls'}
        </button>
        {error && <span className="text-[11px] text-amber-700 dark:text-amber-300">{error}</span>}
      </div>
    );
  }

  // A SESSION ROW OPENS THE MODAL DIRECTLY — the same click AI Calls offers.
  // Nothing is fetched to decide: the row already IS what the modal takes.
  if (sessions !== null) {
    return (
      <>
        <div className="space-y-1.5">
          {sessions.map((sn, i) => (
            <button
              key={sn.id ?? sn.session_ref ?? i}
              type="button"
              onClick={() => setSession(sn)}
              className="w-full text-left px-2 py-1.5 rounded border border-gray-100 dark:border-gray-700
                         flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              <ChevronRight className="w-3 h-3 shrink-0" />
              <span className="text-[12px] text-gray-800 dark:text-gray-100">
                {when(sn.start_time ?? sn.created_at)}
              </span>
              <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {sn.caller_id ?? '—'} → {sn.dialed_number ?? '—'}
              </span>
              {/* The same signal the AI Calls list shows, from the same field. */}
              {(sn.transcript || sn.transcription) && (
                <span className="ml-auto text-[10px] text-indigo-600 dark:text-indigo-400">transcript</span>
              )}
            </button>
          ))}
        </div>
        {session && (
          <AudioSessionModal
            session={session}
            onClose={() => setSession(null)}
            organizationId={organizationId}
          />
        )}
      </>
    );
  }

  return (
    <>
      {calls!.length === 0 ? (
        <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>No calls with this person.</p>
      ) : (
        <div className="space-y-1.5">
          {partial && <p className="text-[11px] text-amber-700 dark:text-amber-300">{partial}</p>}
          {calls!.map((c, i) => (
            <CallEntry key={c.ref || c.callSid || i} call={c} onOpenSession={setSession}
                       organizationId={organizationId} />
          ))}
        </div>
      )}

      {/* The same modal the AI Calls screen uses — so the transcript, the
          recording and the captures all read identically wherever somebody
          arrives from. */}
      {session && (
        <AudioSessionModal
          session={session}
          onClose={() => setSession(null)}
          organizationId={organizationId}
        />
      )}
    </>
  );
};

/** One call. Opens in the shared modal, or inline when there is no session row. */
const CallEntry: React.FC<{
  call: CallRow;
  onOpenSession: (session: any) => void;
  organizationId?: string;
}> = ({ call, onOpenSession, organizationId }) => {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Array<Record<string, unknown>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const openCall = useCallback(async () => {
    if (open) { setOpen(false); return; }
    if (!call.callSid) { setError('This call has no id, so nothing can be looked up for it.'); return; }

    setLoading(true);
    setError(null);
    try {
      // THE SAME ROUTE THE LEADS QUEUE AND AI CALLS TAKE: a session row means
      // the shared modal can open, and it does the rest.
      const res = await sessionsService.getAudioSessions({ callId: call.callSid, perPage: 1 });
      const rows: any[] = (res as any)?.data?.data || (res as any)?.data?.content || [];
      if (rows.length) {
        onOpenSession(rows[0]);
        return;
      }

      // No session row — the NORMAL case for an agent call. The conversation is
      // in agent_call_events instead, and the audio is found from the call id.
      setOpen(true);
      const t = await agentCallsService.transcript(call.callSid);
      if (t.success && t.data) setTurns(t.data.turns || []);
      else setError(t.error || 'Could not load that conversation.');
    } catch {
      setError('Could not open that call.');
    } finally {
      setLoading(false);
    }
  }, [open, call.callSid, onOpenSession]);

  // Resolved only when asked for, through the SHARED resolver — the stored URL
  // on this estate points at the switch, behind HTTP Basic, which a browser
  // cannot play. resolvePlayableUrl decides the order: CDN first when the stored
  // URL is one, the signed cpass copy otherwise.
  const play = useCallback(async () => {
    const url = await recordingsService.resolvePlayableUrl(
      call.callSid, call.recordingUrl, organizationId);
    if (url) setAudioUrl(url);
    else setError('No recording is held for this call.');
  }, [call.callSid, call.recordingUrl, organizationId]);

  return (
    <div className="rounded border border-gray-100 dark:border-gray-700">
      <button
        type="button"
        onClick={openCall}
        disabled={loading}
        className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-gray-50
                   dark:hover:bg-gray-700/40 disabled:opacity-60"
      >
        {loading
          ? <Loader2 className="w-3 h-3 shrink-0 animate-spin" />
          : open ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
        <span className="text-[12px] text-gray-800 dark:text-gray-100">{when(call.at)}</span>
        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
          {call.direction === 'OUTBOUND' ? 'outbound' : 'inbound'}
          {call.durationSeconds != null ? ` · ${duration(call.durationSeconds)}` : ''}
        </span>
        {/* Stated up front, so opening a call with nothing in it is a decision
            rather than a disappointment. */}
        {!call.hasTranscript && (
          <span className="ml-auto text-[10px]" style={{ color: 'var(--text-muted)' }}>no transcript</span>
        )}
      </button>

      {open && (
        <div className="px-2 pb-2 space-y-2">
          {error && <p className="text-[11px] text-amber-700 dark:text-amber-300">{error}</p>}

          {!audioUrl && (
            <button type="button" onClick={play}
                    className="text-[11px] inline-flex items-center gap-1 text-indigo-600
                               dark:text-indigo-400 hover:underline">
              <Play className="w-3 h-3" /> Play the recording
            </button>
          )}

          {/* The player goes IN the conversation, through the viewer's own slot,
              rather than above or below it. */}
          {turns && turns.length > 0 ? (
            <TranscriptViewer
              transcriptJson={JSON.stringify(turns)}
              title="Conversation"
              containerClassName="bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg space-y-2"
              player={audioUrl ? <AudioPlayer src={audioUrl} /> : undefined}
            />
          ) : (
            <>
              {audioUrl && <AudioPlayer src={audioUrl} />}
              {!loading && !error && turns !== null && (
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Nothing was transcribed for this call.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactCalls;
